// Акты на оборудование — зеркало портала 73. ТОЛЬКО ЧТЕНИЕ.
//
// ГЛАВНОЕ ПРО СВЯЗИ, И ЭТО ВИДНО НА ЭКРАНЕ.
// Числа ниже — из запросов к зеркалу `cir_core` 28.07.2026, всего актов 2 630.
// Акты НЕ привязаны к сделкам. В форме портала есть поле «Сделка (необязательно)», но оно
// пусто во ВСЕХ строках, поэтому фильтра «акты по сделке» здесь нет — и он не появится,
// пока связи нет в данных. Судно находится по IMO, и только по нему: у 495 актов IMO пуст,
// у них остаётся лишь текстовое название судна. Придумывать связь через похожие названия —
// значит подсунуть офису акт от другого судна; этого экран не делает.
//
// Большинство актов (2 616 из 2 630 на 28.07.2026) вытащено из архива задач Bitrix, а не
// выпущено на портале: у них engineer_name = «архив Bitrix» и заполнена только часть бланка.
// Это тоже видно.
import { Fragment, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useRefs } from '../api/hooks'
import { useАкт, useАкты, useP73Refs } from '../api/p73'
import type { АктКарточка, АктСтрока, ФильтрыP73 } from '../api/p73'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { PhotoGallery } from '../components/PhotoGallery'
import { PrintButton } from '../components/PrintButton'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, plural } from '../lib/format'
import { листАкта } from '../lib/print73'
import {
  АКТ_ПРОВЕРКИ_ARB, АКТ_ШАБЛОНЫ, АКТ_ШАПКА, ПУТИ, РАЗМЕР_СТРАНИЦЫ, видСтатуса, значениеФормы,
  названиеСтатуса, названиеШаблона, подписьСтрокиАкта,
} from '../lib/p73'

export default function EquipActs() {
  const { id } = useParams<{ id: string }>()
  return id ? <Карточка id={id} /> : <Список />
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [sp, setSp] = useSearchParams()
  // Судно в адресе: сюда будет вести ссылка «акты по судну» из карточки судна.
  const vesselBx = sp.get('vessel_bx') || ''

  const [q, setQ] = useState('')
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  const [archived, setArchived] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const p73 = useP73Refs()

  const фильтры = useMemo<ФильтрыP73>(() => ({
    q: q.trim() || undefined,
    type: type || undefined,
    status: status || undefined,
    archived: archived || undefined,
    vessel_bx: vesselBx || undefined,
    from: from || undefined,
    to: to || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, type, status, archived, vesselBx, from, to, start])

  const запрос = useАкты(фильтры)
  const данные = запрос.data
  const счёт = p73.data?.counts
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="page-head">
        <h1>Акты на оборудование</h1>
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {счёт ? ` · всего в ядре: ${счёт.acts}` : ''}
        </span>
      </div>

      {/* Не «мелкая оговорка внизу», а первое, что видит человек: он придёт сюда искать
          «акты по сделке», и должен сразу понять, почему такого фильтра нет. */}
      <div className="notice">
        <b>Связи акта со сделкой в данных нет.</b> Поле «Сделка» в форме портала не заполнено ни
        в одном акте, поэтому искать акты можно по судну (через IMO), номеру, шаблону и дате —
        но не по сделке. Чтобы найти акты по сделке, откройте её судно и посмотрите его акты.
        {счёт ? <> У {счёт.acts_without_imo}{' '}
          {plural(счёт.acts_without_imo, 'акта', 'актов', 'актов')} из {счёт.acts} пуст и
          IMO — у них связи с карточкой судна нет вовсе, есть только название судна
          текстом.</> : null}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Шесть полей в столбец на телефоне закрывают собой весь список актов,
            поэтому блок сворачивается; применённое перечислено в его заголовке. */}
        <FilterBar id="equip-acts">
          <div className="f" style={{ flex: 2, minWidth: 240 }}>
            <label htmlFor="aq">Номер, судно, IMO или MMSI</label>
            <input id="aq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: ENISEY, 9079169 или 9079169-072026-GMDSS" />
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="atp">Шаблон акта</label>
            <select id="atp" value={type} onChange={(e) => сСброса(setType)(e.target.value)}>
              <option value="">любой</option>
              {/* Порядок и счётчики — из данных ядра: видно, какие бланки вообще есть */}
              {Object.entries(p73.data?.act_type ?? {}).map(([к, n]) => (
                <option key={к} value={к}>{названиеШаблона(к)} ({n})</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="ast">Статус</label>
            <select id="ast" value={status} onChange={(e) => сСброса(setStatus)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(p73.data?.act_status ?? {}).map(([к, n]) => (
                <option key={к} value={к}>{названиеСтатуса(к)} ({n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="aar">Откуда акт</label>
            <select id="aar" value={archived} onChange={(e) => сСброса(setArchived)(e.target.value)}>
              <option value="">любой</option>
              <option value="0">выпущен на портале</option>
              <option value="1">из архива задач Bitrix</option>
            </select>
          </div>

          <div className="f">
            <label htmlFor="afrom">Дата акта с</label>
            <input id="afrom" type="date" value={from} onChange={(e) => сСброса(setFrom)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="ato">по</label>
            <input id="ato" type="date" value={to} onChange={(e) => сСброса(setTo)(e.target.value)} />
          </div>
        </FilterBar>

        {vesselBx && (
          <div className="hint" style={{ marginTop: 10 }}>
            Отфильтровано по судну <EntityLink kind="vessel" id={vesselBx} resolve />{' '}
            <button onClick={() => {
              const next = new URLSearchParams(sp)
              next.delete('vessel_bx')
              setStart(0)
              setSp(next, { replace: true })
            }}>снять</button>
            {' '}— совпадение ищется по IMO судна, а не по названию.
          </div>
        )}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th className="nowrap">Дата</th>
                  <th>Номер</th>
                  <th className="nowrap">Шаблон</th>
                  <th>Судно</th>
                  <th className="nowrap">Кто выпустил</th>
                  <th className="nowrap">Статус</th>
                  <th className="nowrap">Скан</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((a) => <Строка key={a.d1_id} a={a} имяСотрудника={refs.userNameOr} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">Под эти фильтры актов нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {данные && данные.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>← назад</button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
              </span>
              <button disabled={данные.next === null || запрос.isFetching}
                      onClick={() => setStart(данные.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function Строка({ a, имяСотрудника }: {
  a: АктСтрока
  /** refs.userNameOr: имя из справочника ядра, а нет его — имя из учётки портала 73. */
  имяСотрудника: (id?: string | number | null, fallback?: string | null) => string
}) {
  return (
    <tr>
      <td className="mono nowrap" data-label="ID"><Link to={ПУТИ.акт(a.d1_id)}>{a.d1_id}</Link></td>
      <td className="nowrap" data-label="Дата">{fmtDate(a.act_date)}</td>
      <td className="mono" data-label="Номер"><Link to={ПУТИ.акт(a.d1_id)}>{fmtText(a.number)}</Link></td>
      <td className="nowrap" data-label="Шаблон">{названиеШаблона(a.type)}</td>
      <td data-label="Судно">
        {/* Судно нашлось по IMO — ставим ссылку на карточку; не нашлось — показываем то, что
            записано в акте текстом, и говорим, что связи нет. Догадок по названию не делаем. */}
        {a.vessel?.bitrix_id
          ? <EntityLink kind="vessel" id={a.vessel.bitrix_id} title={a.vessel.name || a.vessel_name} />
          : <span>{fmtText(a.vessel_name)}</span>}
        <div className="muted mono">
          {a.imo ? `IMO ${a.imo}` : 'IMO нет — судно в CRM не найти'}
        </div>
      </td>
      <td className="nowrap" data-label="Кто выпустил">
        {a.from_archive
          ? <span className="muted">архив Bitrix</span>
          : имяСотрудника(a.engineer?.bitrix_id, a.engineer?.name || a.engineer_name)}
      </td>
      <td className="nowrap" data-label="Статус"><span className={`badge ${видСтатуса(a.status)}`}>{названиеСтатуса(a.status)}</span></td>
      {/* `has_scan` — булево из ядра, оно известно всегда. «Нет», а не прочерк: прочерк
          в этой колонке читался бы как «не проверяли, есть ли скан». */}
      <td className="nowrap muted" data-label="Скан">{a.has_scan ? 'есть' : 'нет'}</td>
    </tr>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ id }: { id: string }) {
  const запрос = useАкт(id)
  const refs = useRefs()
  const a = запрос.data
  const шаблон = a ? АКТ_ШАБЛОНЫ[a.type] : undefined

  return (
    <>
      <div className="page-head">
        <h1>Акт {a?.number || `#${id}`}</h1>
        <div className="spacer" />
        {/* Печать — бланк портала, собранный по зеркалу. Ничего никуда не заливается:
            номер акта присвоил портал, оригинал и заливка в Bitrix остаются там. */}
        <PrintButton готово={!!a} собрать={(св) => листАкта(a!, { свежесть: св })} />
        <Link to={ПУТИ.акты}>← к списку</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {a && (
          <>
            <div className="grid-2">
              <div>
                <div className="card">
                  <h2>{шаблон?.name || названиеШаблона(a.type)}</h2>
                  <dl className="dl">
                    <dt>Номер</dt><dd className="mono">{fmtText(a.number)}</dd>
                    <dt>Дата акта</dt><dd>{fmtDate(a.act_date)}</dd>
                    <dt>Шаблон</dt>
                    <dd>{названиеШаблона(a.type)} <span className="muted mono">({a.type})</span></dd>
                    <dt>Судно</dt>
                    <dd>
                      {a.vessel?.bitrix_id
                        ? <EntityLink kind="vessel" id={a.vessel.bitrix_id}
                                      title={a.vessel.name || a.vessel_name} />
                        : <span>{fmtText(a.vessel_name)}</span>}
                      {!a.vessel?.bitrix_id && (
                        <div className="hint">
                          {a.imo
                            ? `Судна с IMO ${a.imo} среди судов портала нет — ссылки на карточку нет.`
                            : 'IMO в акте не заполнен, поэтому связи с карточкой судна нет: только название текстом.'}
                        </div>
                      )}
                    </dd>
                    <dt>IMO / MMSI</dt>
                    <dd className="mono">{fmtText(a.imo)} / {fmtText(a.mmsi)}</dd>
                    <dt>Сделка</dt>
                    {/* Поле есть в форме портала, но не заполнено ни в одном акте. Пишем это
                        прямо, а не показываем прочерк: прочерк читался бы как «сделки не было». */}
                    <dd className="muted">
                      {a.deal_number
                        ? a.deal_number
                        : 'в данных не заполнена — портал этого поля не требует, связи акта со сделкой нет'}
                    </dd>
                    <dt>Единиц оборудования</dt>
                    <dd>
                      {fmtText(a.units)}
                      {шаблон ? <span className="muted"> (бланк допускает до {шаблон.units})</span> : null}
                    </dd>
                    <dt>Кто выпустил</dt>
                    <dd>
                      {a.from_archive
                        ? <span className="muted">импорт из архива задач Bitrix (инженер не указан)</span>
                        : refs.userNameOr(a.engineer?.bitrix_id, a.engineer?.name || a.engineer_name)}
                    </dd>
                    <dt>Статус</dt>
                    <dd><span className={`badge ${видСтатуса(a.status)}`}>{названиеСтатуса(a.status)}</span></dd>
                    <dt>Место проверки</dt>
                    {/* Место проверки в источнике не заполняют. Раньше здесь стояло
                        «не заполнено ни у одного акта» — утверждение обо ВСЕЙ таблице в
                        подписи ОДНОЙ строки: заполнись оно у одного акта, остальные
                        строки продолжали бы утверждать «ни у одного». Говорим только про
                        эту запись. */}
                    <dd className="muted">{a.place || 'не заполнено'}</dd>
                    <dt>Создан / изменён</dt>
                    <dd className="muted">{fmtDate(a.created_at, true)} · {fmtDate(a.updated_at, true)}</dd>
                    <dt>Исходный скан</dt>
                    <dd className="muted">
                      {a.has_scan
                        ? <span className="mono">{fmtText(a.file_key)}</span>
                        : 'нет'}
                    </dd>
                  </dl>
                </div>

                <Скан a={a} />
                <Происхождение a={a} />
              </div>

              <div>
                <ШапкаБланка a={a} />
                <СрокиИДаты a={a} />
              </div>
            </div>

            <СтрокиБланка a={a} />
            <ПунктыРС a={a} />
            <Замечания a={a} />
          </>
        )}
      </QueryState>
    </>
  )
}

/**
 * Исходный скан акта. Не фотография, а файл: на 28.07.2026 скан есть у 503 актов из 2 630,
 * и это 251 .docx, 241 .pdf и 11 с прочими расширениями. Показывать нечего — даём скачать,
 * и решает это ядро (поле inline), а не браузер по чужому заголовку.
 *
 * Размера скана в зеркале нет вовсе: p73_equip_acts хранит только file_key, без байтов.
 * Поэтому в плитке будет «—» до нажатия — это «не знаем», а не «пусто».
 */
function Скан({ a }: { a: АктКарточка }) {
  if (!a.has_scan || !a.file_key) return null
  return (
    <div className="card">
      <h2>Исходный скан</h2>
      <PhotoGallery
        вид="act-scan"
        файлы={[{
          // id здесь — id САМОГО акта: своего реестра файлов у актов нет, скан живёт
          // колонкой file_key в строке акта (см. ИСТОЧНИКИ в p73_photo.py).
          d1_id: a.d1_id,
          label: a.file_key.split('/').pop() || a.number || `акт #${a.d1_id}`,
          r2_key: a.file_key,
          size_bytes: null,
          created_at: a.created_at,
          section_key: null,
        }]}
      />
    </div>
  )
}

function Происхождение({ a }: { a: АктКарточка }) {
  const из = a.form.archived_from
  const пред = a.form.prefilled_from
  if (!из && !пред && a.files.length === 0) return null
  return (
    <div className="card">
      <h2>Откуда этот акт</h2>
      <dl className="dl">
        {из && (
          <>
            <dt>Файл в задаче Bitrix</dt><dd className="mono">{fmtText(из.file)}</dd>
            <dt>Задача Bitrix</dt><dd className="mono">{fmtText(из.task)}</dd>
          </>
        )}
        {a.task && (
          <>
            <dt>Название задачи</dt><dd>{fmtText(a.task.title)}</dd>
            <dt>Найдена по слову</dt><dd className="mono">{fmtText(a.task.kw)}</dd>
            <dt>Задача создана</dt><dd className="muted">{fmtDate(a.task.created)}</dd>
          </>
        )}
        {пред && (
          <>
            <dt>Предзаполнен из акта</dt>
            <dd>
              <Link to={ПУТИ.акт(пред.id ?? 0)} className="mono">#{fmtText(пред.id)}</Link>
              <span className="muted"> от {fmtDate(пред.date)}</span>
            </dd>
          </>
        )}
      </dl>
      {a.files.length > 0 && (
        <>
          <h3>Вложения, из которых он сделан</h3>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th className="nowrap">Задача</th>
                  <th className="nowrap">Угадано судно</th>
                  <th className="nowrap">Статус разбора</th>
                </tr>
              </thead>
              <tbody>
                {a.files.map((ф) => (
                  <tr key={ф.d1_id}>
                    <td className="mono" data-label="Файл">{fmtText(ф.file_name)}</td>
                    <td className="mono nowrap" data-label="Задача">{fmtText(ф.task_id)}</td>
                    <td className="nowrap muted" data-label="Угадано судно">{fmtText(ф.vessel_guess)}</td>
                    <td className="nowrap muted" data-label="Статус разбора">{fmtText(ф.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {(из || a.files.length > 0) && (
        <div className="hint" style={{ marginTop: 8 }}>
          Сами вложения остаются в Bitrix: в ядре хранится их идентификатор, но не ссылка —
          в ссылке Bitrix зашит токен доступа, и переносить его в ядро нельзя.
        </div>
      )}
    </div>
  )
}

function ШапкаБланка({ a }: { a: АктКарточка }) {
  const строки = Object.entries(a.form.header || {})
  if (!строки.length) return null
  return (
    <div className="card">
      <h2>Шапка бланка</h2>
      <dl className="dl">
        {строки.map(([к, з]) => (
          <Fragment key={к}>
            <dt>{АКТ_ШАПКА[к] ?? к}</dt>
            <dd>{значениеФормы(з)}</dd>
          </Fragment>
        ))}
      </dl>
      <div className="hint">
        Это то, что напечатано в бланке. Расхождение с карточкой судна в CRM — находка,
        а не ошибка показа.
      </div>
    </div>
  )
}

function СрокиИДаты({ a }: { a: АктКарточка }) {
  const даты = a.form.key_dates
  if (!даты || даты.length === 0) return null
  return (
    <div className="card">
      <h2>Сроки и даты из документа</h2>
      <dl className="dl">
        {даты.map((д, i) => (
          <Fragment key={i}>
            <dt>{д.label}</dt>
            <dd className="mono">{fmtText(д.value)}</dd>
          </Fragment>
        ))}
      </dl>
      <div className="hint">
        Эти строки портал вытащил из документа разбором, а не ввёл инженер вручную. Перед тем
        как опираться на срок, сверьтесь со сканом.
      </div>
    </div>
  )
}

function СтрокиБланка({ a }: { a: АктКарточка }) {
  const строки = Object.entries(a.form.rows || {})
  if (!строки.length) {
    return (
      <div className="card muted">
        Строки бланка в данных пусты. У актов из архива заполнена не вся форма: портал
        вытаскивал из документа то, что смог разобрать.
      </div>
    )
  }
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Форма акта</h2>
        <div className="spacer" />
        <span className="muted">строк заполнено: {строки.length}</span>
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th style={{ width: '45%' }}>Пункт бланка</th>
              <th>Значение</th>
            </tr>
          </thead>
          <tbody>
            {строки.map(([к, з]) => (
              <tr key={к}>
                <td data-label="Пункт бланка">
                  {подписьСтрокиАкта(a.type, к)}
                  <div className="muted mono">{к}</div>
                </td>
                {/* Массив = значение на каждую единицу оборудования (три носимые УКВ) */}
                <td data-label="Значение" style={{ whiteSpace: 'pre-wrap' }}>{значениеФормы(з)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        Подписи пунктов — копия шаблонов портала 73. Если пункт показан кодом, значит бланк
        на портале изменили, а копия ещё нет.
      </div>
    </div>
  )
}

function ПунктыРС({ a }: { a: АктКарточка }) {
  const пункты = Object.entries(a.form.items || {})
  if (!пункты.length) return null
  // Сортировка по номеру пункта: «2.10.1» должен идти после «2.9», а не после «2.1».
  пункты.sort((x, y) => сравнитьНомера(x[0], y[0]))
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Пункты формы 6.3.22.2 РС</h2>
        <div className="spacer" />
        <span className="muted">пунктов: {пункты.length}</span>
      </div>
      {/* Подписи пустые намеренно: шапки у этой таблицы нет и на мониторе — номер пункта
          и отметка стоят рядом без названий. Придумать им названия ради карточек значило
          бы дописать на экран текст, которого в бланке нет. */}
      <div className="table-wrap cards">
        <table>
          <tbody>
            {пункты.map(([к, з]) => (
              <tr key={к}>
                <td className="mono nowrap" data-label="" style={{ width: 160 }}>{к}</td>
                <td data-label="">{значениеФормы(з)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        Формулировки пунктов живут в бланке Регистра, а не в данных: здесь только номер пункта
        и отметка (X — отметка проставлена, NA — не применимо). Портал разбирал эти отметки
        из архивного документа автоматически.
      </div>
    </div>
  )
}

function сравнитьНомера(a: string, b: string): number {
  const ча = a.split(/[^0-9]+/).filter(Boolean).map(Number)
  const чб = b.split(/[^0-9]+/).filter(Boolean).map(Number)
  for (let i = 0; i < Math.max(ча.length, чб.length); i += 1) {
    const x = ча[i] ?? -1
    const y = чб[i] ?? -1
    if (x !== y) return x - y
  }
  return a.localeCompare(b, 'ru')
}

function Замечания({ a }: { a: АктКарточка }) {
  const отметки = Object.entries(a.form.checks || {})
  const есть = отметки.length > 0 || a.form.note || a.form.unknown_keys.length > 0 || a.form.parse_full_fail
  if (!есть) return null
  return (
    <div className="card">
      <h2>Отметки и замечания</h2>
      {отметки.length > 0 && (
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th className="nowrap" style={{ width: 60 }}>Пункт</th>
                <th>Формулировка</th>
                <th className="nowrap">Отметка</th>
              </tr>
            </thead>
            <tbody>
              {отметки.map(([к, з]) => {
                const i = Number(к)
                // Список пунктов есть только у чек-листа АРБ — единственного в шаблонах портала.
                const текст = a.type === 'epirb' && Number.isInteger(i) ? АКТ_ПРОВЕРКИ_ARB[i] : undefined
                return (
                  <tr key={к}>
                    <td className="mono nowrap" data-label="Пункт">{к}</td>
                    <td data-label="Формулировка">
                      {текст || <span className="muted">
                        формулировка пункта есть только в бланке на портале
                      </span>}
                    </td>
                    <td className="nowrap" data-label="Отметка">
                      <span className={`badge ${з === 'no' ? 'lose' : з === 'ok' ? 'won' : ''}`}>
                        {з === 'no' ? 'замечание' : з === 'ok' ? 'норма' : fmtText(з)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {a.form.note ? <div style={{ whiteSpace: 'pre-wrap', marginTop: 10 }}>{a.form.note}</div> : null}
      {a.form.parse_full_fail ? (
        <div className="notice" style={{ marginTop: 10 }}>
          Портал не смог разобрать документ полностью (parse_full_fail =
          {' '}{значениеФормы(a.form.parse_full_fail)}). Часть пунктов бланка в данные не попала —
          смотрите скан.
        </div>
      ) : null}
      {a.form.unknown_keys.length > 0 && (
        <div className="hint" style={{ marginTop: 10 }}>
          В форме есть ключи, которых этот раздел не знает: {a.form.unknown_keys.join(', ')}.
        </div>
      )}
    </div>
  )
}
